import type { ScpData, SearchLinks } from "@hobbyshopify/backend-client";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { gradeLabel, gradePriceText } from "../core/primitives/grades";
import { EbayGradeLinks } from "./EbayGradeLinks";
import { gradeLinkUrl, quickLinkGrades } from "./ebay-links";
import { useModalLayer } from "./ModalLayer";
import { ArrowUpRightIcon, GridIcon } from "./review-icons";

/** Every grade, over the page, focus on Done. */
function AllGradesDialog({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const done = useRef<HTMLButtonElement>(null);
  useModalLayer(done, onClose);

  return createPortal(
    <div className="scrim items-end justify-center sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`eBay sold prices for ${title}`}
        className="modal relative max-h-[85dvh] w-full max-w-md overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-5"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <p className="min-w-0 text-sm font-semibold text-white">{title}</p>
          <button ref={done} type="button" onClick={onClose} className="btn-ghost shrink-0 text-accent">
            Done
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/**
 * eBay sold comps on a review row: RAW, the card's own grade (marked) and PSA 10,
 * each with its comp, and every other grade one tap away.
 *
 * On the row because opening every card to see its comps is what made checking
 * 64 cards for one order take half an hour (HB-I049). A port of the iOS
 * `AdminEbayQuickLinks`. Hidden when there is no title to search eBay for.
 */
export function EbayQuickLinks({
  title,
  ownKey,
  pricedKey,
  searchLinks,
  scpData,
}: {
  title: string;
  /** The card's own grade, marked; null for a raw card or one nobody can name. */
  ownKey: string | null;
  /** The grade the prefilled price was read from, marked in the full grid. */
  pricedKey: string | null;
  searchLinks: SearchLinks | null;
  scpData: ScpData | null;
}) {
  const [allOpen, setAllOpen] = useState(false);
  // Stable, so the dialog does not take the focus back on every render of the row.
  const closeAll = useCallback(() => setAllOpen(false), []);
  if (!title.trim()) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex gap-0.5 rounded-btn bg-surface p-1">
        {quickLinkGrades(ownKey).map((key) => {
          const own = key === ownKey;
          const label = gradeLabel(key);
          const price = gradePriceText(key, scpData);
          return (
            <a
              key={key}
              href={gradeLinkUrl(key, searchLinks, title) ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`eBay sold, ${label}${price ? ` ${price}` : ""}`}
              className={`flex h-10 min-w-0 flex-1 flex-col items-center justify-center rounded-md leading-tight transition-colors ${
                own ? "bg-surface-strong text-accent" : "text-muted hover:text-white"
              }`}
            >
              <span className="flex max-w-full items-center gap-1 text-[13px] font-semibold">
                {own && <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-accent" aria-hidden="true" />}
                <span className="truncate">{label}</span>
              </span>
              {price && <span className="max-w-full truncate text-[11px]">{price}</span>}
            </a>
          );
        })}
        <button
          type="button"
          onClick={() => setAllOpen(true)}
          aria-label="All eBay grades"
          className="flex h-10 w-11 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:text-white"
        >
          <GridIcon />
        </button>
      </div>
      <p className="flex items-center gap-1 px-1 text-[11px] text-muted">
        eBay sold <ArrowUpRightIcon />
      </p>

      {allOpen && (
        <AllGradesDialog title={title} onClose={closeAll}>
          <EbayGradeLinks
            searchLinks={searchLinks ?? undefined}
            scpData={scpData}
            pricedGradeKey={pricedKey}
            cardGradeKey={ownKey}
            cardTitle={title}
          />
        </AllGradesDialog>
      )}
    </div>
  );
}
